import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Badge } from "./badge";
import { Filter, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

export interface FilterOptions {
  sport?: string[];
  platform?: string[];
  grade?: string[];
  subject?: string[];
  parallel?: string[];
  minPrice?: number;
  maxPrice?: number;
  startDate?: string;
  endDate?: string;
  year?: string[];
}

interface QuickFiltersProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  transactions?: any[];
}

const QuickFilters: React.FC<QuickFiltersProps> = ({
  open,
  onClose,
  onApplyFilters,
  transactions = [],
}) => {
  const [localFilters, setLocalFilters] = useState<FilterOptions>({});

  // Extract unique values for filter options
  const uniqueValues = useMemo(() => {
    const sports = new Set<string>();
    const platforms = new Set<string>();
    const grades = new Set<string>();
    const years = new Set<string>();
    const subjects = new Set<string>();
    const parallels = new Set<string>();

    transactions.forEach((t) => {
      if (t.sport) sports.add(t.sport);
      if (t.purchase_platform) platforms.add(t.purchase_platform);
      if (t.grade) grades.add(t.grade);
      if (t.year) years.add(String(t.year));
      if (t.subject) subjects.add(t.subject);
      if (t.parallel) parallels.add(t.parallel);
    });

    return {
      sports: Array.from(sports).sort(),
      platforms: Array.from(platforms).sort(),
      grades: Array.from(grades).sort(),
      years: Array.from(years).sort(),
      subjects: Array.from(subjects).sort(),
      parallels: Array.from(parallels).sort(),
    };
  }, [transactions]);

  const handleApplyFilters = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    setLocalFilters({});
    onApplyFilters({});
    onClose();
  };

  // Toggle multi-select filter values
  const toggleFilterArray = (filterKey: keyof FilterOptions, value: string) => {
    const currentArray = localFilters[filterKey] as string[] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value];
    
    setLocalFilters(prev => ({
      ...prev,
      [filterKey]: newArray.length > 0 ? newArray : undefined
    }));
  };

  // Helper to get display text for multi-select dropdown
  const getDisplayText = (values: string[] | undefined, allValues: string[]) => {
    if (!values || values.length === 0) return `All`;
    if (values.length === allValues.length) return `All`;
    return values.join(', ');
  };

  const activeFilterCount = Object.keys(localFilters).filter(key => {
    const value = localFilters[key as keyof FilterOptions];
    if (value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value !== '';
    if (typeof value === 'number') return value !== 0;
    return true;
  }).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            Quick Filters
          </DialogTitle>
          <DialogDescription>
            Filter your inventory by multiple criteria. Multi-select lists use AND logic together with price and date ranges.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Sport Filter */}
          <div className="space-y-2">
            <Label>Sport / TCG</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getDisplayText(localFilters.sport, uniqueValues.sports)}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                {uniqueValues.sports.map((sport) => (
                  <DropdownMenuCheckboxItem
                    key={sport}
                    checked={(localFilters.sport || []).includes(sport)}
                    onCheckedChange={() => toggleFilterArray('sport', sport)}
                  >
                    {sport}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Platform Filter */}
          <div className="space-y-2">
            <Label>Platform</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getDisplayText(localFilters.platform, uniqueValues.platforms)}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                {uniqueValues.platforms.map((platform) => (
                  <DropdownMenuCheckboxItem
                    key={platform}
                    checked={(localFilters.platform || []).includes(platform)}
                    onCheckedChange={() => toggleFilterArray('platform', platform)}
                  >
                    {platform}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Grade Filter */}
          <div className="space-y-2">
            <Label>Grade</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getDisplayText(localFilters.grade, uniqueValues.grades)}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                {uniqueValues.grades.map((grade) => (
                  <DropdownMenuCheckboxItem
                    key={grade}
                    checked={(localFilters.grade || []).includes(grade)}
                    onCheckedChange={() => toggleFilterArray('grade', grade)}
                  >
                    {grade}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Player / Character */}
          <div className="space-y-2">
            <Label>Player / Character</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getDisplayText(localFilters.subject, uniqueValues.subjects)}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                {uniqueValues.subjects.map((subject) => (
                  <DropdownMenuCheckboxItem
                    key={subject}
                    checked={(localFilters.subject || []).includes(subject)}
                    onCheckedChange={() => toggleFilterArray("subject", subject)}
                  >
                    {subject}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Parallel */}
          <div className="space-y-2">
            <Label>Parallel</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getDisplayText(localFilters.parallel, uniqueValues.parallels)}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                {uniqueValues.parallels.map((parallel) => (
                  <DropdownMenuCheckboxItem
                    key={parallel}
                    checked={(localFilters.parallel || []).includes(parallel)}
                    onCheckedChange={() => toggleFilterArray("parallel", parallel)}
                  >
                    {parallel}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Price Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min-price">Min Price</Label>
              <Input
                id="min-price"
                type="number"
                step="0.01"
                value={localFilters.minPrice || ""}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    minPrice: e.target.value ? parseFloat(e.target.value) : undefined,
                  }))
                }
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-price">Max Price</Label>
              <Input
                id="max-price"
                type="number"
                step="0.01"
                value={localFilters.maxPrice || ""}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    maxPrice: e.target.value ? parseFloat(e.target.value) : undefined,
                  }))
                }
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={localFilters.startDate || ""}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    startDate: e.target.value || undefined,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={localFilters.endDate || ""}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    endDate: e.target.value || undefined,
                  }))
                }
              />
            </div>
          </div>

          {/* Year Filter */}
          <div className="space-y-2">
            <Label>Year</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  {getDisplayText(localFilters.year, uniqueValues.years)}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-y-auto">
                {uniqueValues.years.map((year) => (
                  <DropdownMenuCheckboxItem
                    key={year}
                    checked={(localFilters.year || []).includes(year)}
                    onCheckedChange={() => toggleFilterArray('year', year)}
                  >
                    {year}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Active Filters Indicator */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 p-3 glass-card rounded-lg">
              <Badge variant="secondary" className="text-sm">
                {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
              </Badge>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClearFilters}>
            Clear All
          </Button>
          <Button variant="gradient" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { QuickFilters };

